import type { BuyerLoiState, FunnelStage } from '@dataroom/shared';

/**
 * What the buyer's LOI card shows (ADR-0029: the buyer uploads their signed LOI;
 * the seller side records Accepted / Countered / Rejected), decided from the
 * buyer's LATEST LOI (GET /rooms/:id/loi) and their funnel stage. The rules
 * mirror the API: a first LOI needs the `cim` stage; after a countered or
 * rejected round the buyer may upload a fresh one, from `cim` or `loi_submitted`
 * (the first upload moved them there). A legacy typed LOI the buyer never
 * signed (`awaiting_buyer_signature`) was never a submission — it reads as no LOI.
 */
export type LoiCard =
  | { kind: 'locked' } // not at the LOI stage yet
  | { kind: 'upload' } // no LOI yet, CIM unlocked
  | { kind: 'status'; loi: BuyerLoiState; canResubmit: boolean }
  | { kind: 'past' }; // no LOI on record but past the LOI stage (an admin override)

const PAST_LOI: readonly FunnelStage[] = [
  'loi_submitted',
  'due_diligence',
  'exclusivity_expires',
  'closing',
];

export function loiCard(stage: FunnelStage, loi: BuyerLoiState | null | undefined): LoiCard {
  const mayUpload = stage === 'cim' || stage === 'loi_submitted';
  if (loi && loi.status !== 'awaiting_buyer_signature') {
    const roundClosed = loi.status === 'redlined' || loi.status === 'rejected';
    return { kind: 'status', loi, canResubmit: roundClosed && mayUpload };
  }
  // No real submission yet: a first LOI from `cim`; a legacy unsigned one is
  // released, so its buyer may upload from `loi_submitted` too.
  if (stage === 'cim' || (loi && mayUpload)) return { kind: 'upload' };
  if (PAST_LOI.includes(stage)) return { kind: 'past' };
  return { kind: 'locked' };
}
